import React, { useState, type JSX } from "react";
import ChatInput from "../../ChatInput_Component";
import ChatTranscript, { type ChatTranscriptProps } from "./ChatTranscript";
import { LimitationsNotice } from "./ChatOverlays";
import { USE_CASE_LIMITATIONS } from "./chatbotConstants";
import { useIsTablet } from "../../hooks/useIsTablet";
import { useVirtualKeyboard } from "../../hooks/useVirtualKeyboard";

/** Extra room reclaimed above the input when the tablet keyboard is open. */
const KEYBOARD_SPACER_HEIGHT = "30px";

export interface ChatBodyProps extends ChatTranscriptProps {
  chatInputAriaLabel: string;
}

/** The chat surface once conversation history has finished loading. */
export default function ChatBody({
  chatInputAriaLabel,
  ...transcript
}: ChatBodyProps): JSX.Element {
  const { chat, useCase } = transcript;
  const [inputValue, setInputValue] = useState<string>("");
  const isTablet = useIsTablet();
  const { isKeyboardOpen } = useVirtualKeyboard({ enabled: isTablet });

  const showLimitations =
    useCase.useCaseSelected &&
    !useCase.limitationsDismissed &&
    chat.userMessageCount === 0 &&
    Boolean(useCase.selectedUseCase && USE_CASE_LIMITATIONS[useCase.selectedUseCase]);

  return (
    <>
      <ChatTranscript {...transcript} />

      {/* Feature-specific limitations info bubble */}
      {showLimitations && (
        <LimitationsNotice
          selectedUseCase={useCase.selectedUseCase}
          onDismiss={useCase.dismissLimitations}
        />
      )}

      {/* Tablet keyboard spacer - compresses dead space when the keyboard is open */}
      {isTablet && chat.showConversationStarters && (
        <div
          className="shrink transition-all duration-200"
          style={{ minHeight: isKeyboardOpen ? KEYBOARD_SPACER_HEIGHT : undefined }}
        />
      )}

      <div>
        <ChatInput ariaLabel={chatInputAriaLabel} value={inputValue} onChange={setInputValue} />
      </div>
    </>
  );
}
